import api from '@/services/api';
import type {
  AlertAttributionQueryRequest,
  AlertAttributionReply,
  AlertType,
  AttributionUserLookupReply,
  GenericAttributionEventPage,
  GenericAttributionReply,
  HTTPFailureEventPage,
  HTTPFailureEventPageRequest,
} from '@/types/alertAttribution';

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord {
  return value && typeof value === 'object' ? (value as UnknownRecord) : {};
}

function pick<T>(
  value: UnknownRecord,
  camel: string,
  snake: string,
  fallback: T,
): T {
  return (value[camel] ?? value[snake] ?? fallback) as T;
}

function normalizeDistribution(value: unknown) {
  return Array.isArray(value)
    ? value.map((item) => {
        const row = record(item);
        return {
          value: String(row.value ?? ''),
          count: Number(row.count ?? 0),
        };
      })
    : [];
}

function normalizeDevice(value: unknown) {
  const row = record(value);
  return {
    userDeviceId: String(pick(row, 'userDeviceId', 'user_device_id', '')),
    userId: String(pick(row, 'userId', 'user_id', '')),
    deviceId: String(pick(row, 'deviceId', 'device_id', '')),
    displayName: String(pick(row, 'displayName', 'display_name', '')),
    reportedName: String(pick(row, 'reportedName', 'reported_name', '')),
    platform: String(row.platform ?? ''),
    arch: String(row.arch ?? ''),
    clientVersion: String(pick(row, 'clientVersion', 'client_version', '')),
    identityRegistered: Boolean(
      pick(row, 'identityRegistered', 'identity_registered', false),
    ),
    firstSeenAt:
      String(pick(row, 'firstSeenAt', 'first_seen_at', '')) || undefined,
    lastSeenAt:
      String(pick(row, 'lastSeenAt', 'last_seen_at', '')) || undefined,
    updatedAt: String(pick(row, 'updatedAt', 'updated_at', '')) || undefined,
    resolveStatus: String(pick(row, 'resolveStatus', 'resolve_status', '')),
    attributionConfirmed: Boolean(
      pick(row, 'attributionConfirmed', 'attribution_confirmed', false),
    ),
  };
}

function normalizeUser(value: unknown) {
  const row = record(value);
  return {
    userId: String(pick(row, 'userId', 'user_id', '')),
    name: String(row.name ?? ''),
    email: String(row.email ?? ''),
    avatar: String(row.avatar ?? ''),
    resolveStatus: String(pick(row, 'resolveStatus', 'resolve_status', '')),
  };
}

function normalizeTrendSeries(value: unknown) {
  return Array.isArray(value)
    ? value.map((value) => {
        const row = record(value);
        const points = pick<unknown[]>(row, 'points', 'points', []);
        return {
          key: String(row.key ?? ''),
          label: String(row.label ?? ''),
          statusCode: Number(pick(row, 'statusCode', 'status_code', 0)),
          value: String(row.value ?? ''),
          points: points.map((value) => {
            const point = record(value);
            return {
              timestamp: String(point.timestamp ?? ''),
              count: Number(point.count ?? 0),
            };
          }),
        };
      })
    : [];
}

function normalizeReply(value: unknown): AlertAttributionReply {
  const root = record(value);
  const window = record(root.window);
  const summary = record(root.summary);
  const missing = record(pick(root, 'missingFields', 'missing_fields', {}));
  const stats = record(pick(root, 'queryStats', 'query_stats', {}));
  const internalUserFilter = record(
    pick(root, 'internalUserFilter', 'internal_user_filter', {}),
  );
  const actorsRaw = pick<unknown[]>(root, 'actors', 'actors', []);
  const roomsRaw = pick<unknown[]>(root, 'rooms', 'rooms', []);
  const eventsRaw = pick<unknown[]>(root, 'events', 'events', []);
  const trendSeriesRaw = pick<unknown[]>(
    root,
    'trendSeries',
    'trend_series',
    [],
  );
  const trendGroupsRaw = pick<unknown[]>(
    root,
    'trendGroups',
    'trend_groups',
    [],
  );
  return {
    firstPageToken: String(
      pick(root, 'firstPageToken', 'first_page_token', ''),
    ),
    window: {
      from: String(window.from ?? ''),
      to: String(window.to ?? ''),
      statusClass: String(pick(window, 'statusClass', 'status_class', '')),
    },
    summary: {
      total: Number(summary.total ?? 0),
      uniqueUsers: Number(pick(summary, 'uniqueUsers', 'unique_users', 0)),
      uniqueDevices: Number(
        pick(summary, 'uniqueDevices', 'unique_devices', 0),
      ),
      uniqueRooms: Number(pick(summary, 'uniqueRooms', 'unique_rooms', 0)),
    },
    missingFields: {
      userId: Number(pick(missing, 'userId', 'user_id', 0)),
      deviceId: Number(pick(missing, 'deviceId', 'device_id', 0)),
      roomId: Number(pick(missing, 'roomId', 'room_id', 0)),
      clientVersion: Number(
        pick(missing, 'clientVersion', 'client_version', 0),
      ),
    },
    actors: actorsRaw.map((value) => {
      const row = record(value);
      const user = record(row.user);
      return {
        count: Number(row.count ?? 0),
        user: {
          userId: String(pick(user, 'userId', 'user_id', '')),
          name: String(user.name ?? ''),
          email: String(user.email ?? ''),
          avatar: String(user.avatar ?? ''),
          resolveStatus: String(
            pick(user, 'resolveStatus', 'resolve_status', ''),
          ),
        },
        device: normalizeDevice(row.device),
        candidateDevices: (
          pick<unknown[]>(row, 'candidateDevices', 'candidate_devices', []) ??
          []
        ).map(normalizeDevice),
        topRoomId: String(pick(row, 'topRoomId', 'top_room_id', '')),
        topRoomCount: Number(pick(row, 'topRoomCount', 'top_room_count', 0)),
        topIssue: String(pick(row, 'topIssue', 'top_issue', '')),
        topIssueCount: Number(pick(row, 'topIssueCount', 'top_issue_count', 0)),
        eventClientVersions: pick<string[]>(
          row,
          'eventClientVersions',
          'event_client_versions',
          [],
        ),
      };
    }),
    rooms: roomsRaw.map((value) => {
      const row = record(value);
      return {
        roomId: String(pick(row, 'roomId', 'room_id', '')),
        name: String(row.name ?? ''),
        ownerUserId: String(pick(row, 'ownerUserId', 'owner_user_id', '')),
        deleted: Boolean(row.deleted),
        updatedAt:
          String(pick(row, 'updatedAt', 'updated_at', '')) || undefined,
        resolveStatus: String(pick(row, 'resolveStatus', 'resolve_status', '')),
        count: Number(row.count ?? 0),
      };
    }),
    events: eventsRaw.map((value) => {
      const row = record(value);
      return {
        timestamp: String(row.timestamp ?? '') || undefined,
        statusCode: Number(pick(row, 'statusCode', 'status_code', 0)),
        method: String(row.method ?? ''),
        route: String(row.route ?? ''),
        urlPath: String(pick(row, 'urlPath', 'url_path', '')),
        reason: String(row.reason ?? ''),
        errorMessage: String(pick(row, 'errorMessage', 'error_message', '')),
        requestId: String(pick(row, 'requestId', 'request_id', '')),
        requestParamsJson: String(
          pick(row, 'requestParamsJson', 'request_params_json', ''),
        ),
        userId: String(pick(row, 'userId', 'user_id', '')),
        deviceId: String(pick(row, 'deviceId', 'device_id', '')),
        deviceName: String(pick(row, 'deviceName', 'device_name', '')),
        roomId: String(pick(row, 'roomId', 'room_id', '')),
        clientVersion: String(pick(row, 'clientVersion', 'client_version', '')),
        platform: String(row.platform ?? ''),
        arch: String(row.arch ?? ''),
        userAgent: String(pick(row, 'userAgent', 'user_agent', '')),
        clientIp: String(pick(row, 'clientIp', 'client_ip', '')),
      };
    }),
    statusDistribution: normalizeDistribution(
      pick(root, 'statusDistribution', 'status_distribution', []),
    ),
    routeDistribution: normalizeDistribution(
      pick(root, 'routeDistribution', 'route_distribution', []),
    ),
    reasonDistribution: normalizeDistribution(
      pick(root, 'reasonDistribution', 'reason_distribution', []),
    ),
    clientVersionDistribution: normalizeDistribution(
      pick(
        root,
        'clientVersionDistribution',
        'client_version_distribution',
        [],
      ),
    ),
    trendBucketSeconds: Number(
      pick(root, 'trendBucketSeconds', 'trend_bucket_seconds', 0),
    ),
    trendGroupBucketSeconds: Number(
      pick(root, 'trendGroupBucketSeconds', 'trend_group_bucket_seconds', 0),
    ),
    trendSeries: normalizeTrendSeries(trendSeriesRaw),
    trendGroups: trendGroupsRaw.map((value) => {
      const row = record(value);
      return {
        groupBy: String(
          pick(
            row,
            'groupBy',
            'group_by',
            'HTTP_FAILURE_TREND_GROUP_BY_UNSPECIFIED',
          ),
        ) as AlertAttributionReply['trendGroups'][number]['groupBy'],
        series: normalizeTrendSeries(row.series),
      };
    }),
    queryStats: {
      recordsScanned: Number(
        pick(stats, 'recordsScanned', 'records_scanned', 0),
      ),
      recordsMatched: Number(
        pick(stats, 'recordsMatched', 'records_matched', 0),
      ),
      bytesScanned: Number(pick(stats, 'bytesScanned', 'bytes_scanned', 0)),
      queryDurationMs: Number(
        pick(stats, 'queryDurationMs', 'query_duration_ms', 0),
      ),
    },
    internalUserFilter: {
      enabled: Boolean(internalUserFilter.enabled),
      excludedEvents: Number(
        pick(internalUserFilter, 'excludedEvents', 'excluded_events', 0),
      ),
      excludedUsers: Number(
        pick(internalUserFilter, 'excludedUsers', 'excluded_users', 0),
      ),
      source: String(internalUserFilter.source ?? ''),
    },
    truncated: Boolean(root.truncated),
    eventsTruncated: Boolean(
      pick(root, 'eventsTruncated', 'events_truncated', false),
    ),
    warnings: Array.isArray(root.warnings)
      ? root.warnings.map((item) => String(item))
      : [],
  };
}

function normalizeAlertType(value: unknown): AlertType {
  const row = record(value);
  return {
    uid: String(row.uid ?? ''),
    title: String(row.title ?? ''),
    severity: String(row.severity ?? ''),
    service: String(row.service ?? ''),
    folderUid: String(pick(row, 'folderUid', 'folder_uid', '')),
    ruleGroup: String(pick(row, 'ruleGroup', 'rule_group', '')),
    attributionKind: String(
      pick(
        row,
        'attributionKind',
        'attribution_kind',
        'ATTRIBUTION_KIND_UNSPECIFIED',
      ),
    ) as AlertType['attributionKind'],
    httpStatusClass: String(
      pick(row, 'httpStatusClass', 'http_status_class', ''),
    ),
    attributionSupported: Boolean(
      pick(row, 'attributionSupported', 'attribution_supported', false),
    ),
    attributionQuery: String(
      pick(row, 'attributionQuery', 'attribution_query', ''),
    ),
    diagnosticPrefix: String(
      pick(row, 'diagnosticPrefix', 'diagnostic_prefix', ''),
    ),
    source: String(
      pick(row, 'source', 'source', 'ALERT_SOURCE_UNSPECIFIED'),
    ) as AlertType['source'],
    region: String(row.region ?? ''),
  };
}

function normalizeFields(value: unknown) {
  return Array.isArray(value)
    ? value.map((item) => {
        const row = record(item);
        return {
          key: String(row.key ?? ''),
          value: String(row.value ?? ''),
          state: String(row.state ?? ''),
        };
      })
    : [];
}

function normalizeGenericReply(value: unknown): GenericAttributionReply {
  const root = record(value);
  const stats = record(pick(root, 'queryStats', 'query_stats', {}));
  return {
    alertType: normalizeAlertType(pick(root, 'alertType', 'alert_type', {})),
    from: String(root.from ?? ''),
    to: String(root.to ?? ''),
    groups: (pick<unknown[]>(root, 'groups', 'groups', []) ?? []).map(
      (value) => {
        const row = record(value);
        return {
          count: Number(row.count ?? 0),
          identifiers: normalizeFields(row.identifiers),
          evidence: normalizeFields(row.evidence),
          summary: String(row.summary ?? ''),
        };
      },
    ),
    events: (pick<unknown[]>(root, 'events', 'events', []) ?? []).map(
      (value) => {
        const row = record(value);
        return {
          timestamp: String(row.timestamp ?? '') || undefined,
          source: String(row.source ?? ''),
          identifiers: normalizeFields(row.identifiers),
          evidence: normalizeFields(row.evidence),
        };
      },
    ),
    queryStats: {
      recordsScanned: Number(
        pick(stats, 'recordsScanned', 'records_scanned', 0),
      ),
      recordsMatched: Number(
        pick(stats, 'recordsMatched', 'records_matched', 0),
      ),
      bytesScanned: Number(pick(stats, 'bytesScanned', 'bytes_scanned', 0)),
      queryDurationMs: Number(
        pick(stats, 'queryDurationMs', 'query_duration_ms', 0),
      ),
    },
    truncated: Boolean(root.truncated),
    eventsTruncated: Boolean(
      pick(root, 'eventsTruncated', 'events_truncated', false),
    ),
    warnings: Array.isArray(root.warnings)
      ? root.warnings.map((item) => String(item))
      : [],
  };
}

function toAPIRequest(request: AlertAttributionQueryRequest) {
  return {
    from: request.from,
    to: request.to,
    statusClass:
      request.statusClass === '5xx'
        ? 'HTTP_STATUS_CLASS_5XX'
        : request.statusClass === '4xx'
          ? 'HTTP_STATUS_CLASS_4XX'
          : 'HTTP_STATUS_CLASS_ALL',
    filters: {
      statusCodes: request.filters?.statusCodes ?? [],
      routes:
        request.filters?.routes ??
        (request.filters?.route ? [request.filters.route] : []),
      reasons:
        request.filters?.reasons ??
        (request.filters?.reason ? [request.filters.reason] : []),
      userIds:
        request.filters?.userIds ??
        (request.filters?.userId ? [request.filters.userId] : []),
      deviceIds:
        request.filters?.deviceIds ??
        (request.filters?.deviceId ? [request.filters.deviceId] : []),
      roomIds:
        request.filters?.roomIds ??
        (request.filters?.roomId ? [request.filters.roomId] : []),
      clientVersions:
        request.filters?.clientVersions ??
        (request.filters?.clientVersion ? [request.filters.clientVersion] : []),
      requestPaths: request.filters?.requestPaths ?? [],
      requestPathMatchMode:
        request.filters?.requestPathMatchMode === 'contains'
          ? 'HTTP_PATH_MATCH_MODE_CONTAINS'
          : request.filters?.requestPathMatchMode === 'regex'
            ? 'HTTP_PATH_MATCH_MODE_REGEX'
            : 'HTTP_PATH_MATCH_MODE_EXACT',
    },
    detailLimit: request.detailLimit ?? 200,
    includeSensitive: request.includeSensitive ?? false,
    summaryOnly: request.summaryOnly ?? false,
    includeEventCursor: request.includeEventCursor ?? false,
    excludeInternalUsers: request.excludeInternalUsers ?? false,
  };
}

function normalizeHTTPFailureEventPage(value: unknown): HTTPFailureEventPage {
  const root = record(value);
  const normalized = normalizeReply(root);
  return {
    window: normalized.window,
    events: normalized.events,
    actors: normalized.actors,
    rooms: normalized.rooms,
    nextPageToken: String(pick(root, 'nextPageToken', 'next_page_token', '')),
    hasMore: Boolean(pick(root, 'hasMore', 'has_more', false)),
    cursorExpiresAt:
      String(pick(root, 'cursorExpiresAt', 'cursor_expires_at', '')) ||
      undefined,
    queryStats: normalized.queryStats,
    internalUserFilter: normalized.internalUserFilter,
    sourceTruncated: Boolean(
      pick(root, 'sourceTruncated', 'source_truncated', false),
    ),
    warnings: normalized.warnings,
  };
}

export const alertAttributionApi = {
  listAlertTypes: async (): Promise<{
    alertTypes: AlertType[];
    warnings: string[];
  }> => {
    const response = record(
      await api.get('/api/v1/alert-attributions/alert-types'),
    );
    const values = pick<unknown[]>(response, 'alertTypes', 'alert_types', []);
    return {
      alertTypes: values.map(normalizeAlertType),
      warnings: Array.isArray(response.warnings)
        ? response.warnings.map((warning) => String(warning))
        : [],
    };
  },
  queryGeneric: async (request: {
    from: string;
    to: string;
    alertUid: string;
    detailLimit?: number;
    includeSensitive?: boolean;
    summaryOnly?: boolean;
  }): Promise<GenericAttributionReply> => {
    const response = await api.post('/api/v1/alert-attributions/query', {
      from: request.from,
      to: request.to,
      alertUid: request.alertUid,
      detailLimit: request.detailLimit ?? 200,
      includeSensitive: request.includeSensitive ?? false,
      summaryOnly: request.summaryOnly ?? false,
    });
    return normalizeGenericReply(response);
  },
  queryGenericEvents: async (request: {
    from: string;
    to: string;
    alertUid: string;
    pageSize?: number;
    pageToken?: string;
    includeSensitive?: boolean;
  }): Promise<GenericAttributionEventPage> => {
    const root = record(
      await api.post('/api/v1/alert-attributions/events/query', {
        from: request.from,
        to: request.to,
        alertUid: request.alertUid,
        pageSize: request.pageSize ?? 20,
        pageToken: request.pageToken ?? '',
        includeSensitive: request.includeSensitive ?? false,
      }),
    );
    const normalized = normalizeGenericReply(root);
    return {
      alertType: normalized.alertType,
      from: normalized.from,
      to: normalized.to,
      events: normalized.events,
      nextPageToken: String(pick(root, 'nextPageToken', 'next_page_token', '')),
      hasMore: Boolean(pick(root, 'hasMore', 'has_more', false)),
      cursorExpiresAt:
        String(pick(root, 'cursorExpiresAt', 'cursor_expires_at', '')) ||
        undefined,
      queryStats: normalized.queryStats,
      sourceTruncated: Boolean(
        pick(root, 'sourceTruncated', 'source_truncated', false),
      ),
      warnings: normalized.warnings,
    };
  },
  queryHTTPFailures: async (
    request: AlertAttributionQueryRequest,
  ): Promise<AlertAttributionReply> => {
    const response = await api.post(
      '/api/v1/alert-attributions/http-failures/query',
      toAPIRequest(request),
    );
    return normalizeReply(response);
  },
  queryHTTPFailureEvents: async (
    request: HTTPFailureEventPageRequest,
  ): Promise<HTTPFailureEventPage> => {
    const response = await api.post(
      '/api/v1/alert-attributions/http-failures/events/query',
      {
        ...toAPIRequest(request),
        detailLimit: undefined,
        summaryOnly: undefined,
        includeEventCursor: undefined,
        pageSize: request.pageSize ?? 20,
        pageToken: request.pageToken ?? '',
      },
    );
    return normalizeHTTPFailureEventPage(response);
  },
  lookupUser: async (request: {
    lookupType: 'userId' | 'deviceId';
    lookupValue: string;
    includeSensitive?: boolean;
  }): Promise<AttributionUserLookupReply> => {
    const response = record(
      await api.post('/api/v1/alert-attributions/users/lookup', {
        userId: request.lookupType === 'userId' ? request.lookupValue : '',
        deviceId: request.lookupType === 'deviceId' ? request.lookupValue : '',
        includeSensitive: request.includeSensitive ?? false,
      }),
    );
    const results = pick<unknown[]>(response, 'results', 'results', []);
    return {
      lookupType: String(
        pick(response, 'lookupType', 'lookup_type', request.lookupType),
      ) as AttributionUserLookupReply['lookupType'],
      lookupValue: String(
        pick(response, 'lookupValue', 'lookup_value', request.lookupValue),
      ),
      results: results.map((value) => {
        const row = record(value);
        return {
          user: normalizeUser(row.user),
          devices: pick<unknown[]>(row, 'devices', 'devices', []).map(
            normalizeDevice,
          ),
        };
      }),
      warnings: Array.isArray(response.warnings)
        ? response.warnings.map((warning) => String(warning))
        : [],
    };
  },
};
